import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import RootState from '../../redux/states/root.state';
import Spinner from "../common/Spinner";
import usePermissionCheck from '../../hooks/usePermissionCheck';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const AuthRoute: React.FC<ProtectedRouteProps> = ({children}) => {
    const {data:user, loading, refreshing} = useSelector((state: RootState) => state.user);
    const {canAccess, checking} = usePermissionCheck(['view_dashboard']);
    const navigate = useNavigate();
    const location = useLocation();

    // Check if there's a token in localStorage
    const hasToken = !!localStorage.getItem('accessToken');

    useEffect(() => {
        // If user is authenticated, send them to their appropriate homepage
        if (user && !checking) {
            const from = (location.state as { from?: string } | null)?.from;
            const fallback = canAccess ? "/home" : "/my-spas";
            navigate(from || fallback, { replace: true });
        }
    }, [user, navigate, canAccess, checking, location.state]);

    // If there's no token, show the page immediately (don't wait for loading)
    if (!hasToken) {
        return <>{children}</>;
    }

    // If there's a token, show spinner while loading/refreshing
    if (loading || refreshing || checking) {
        return <Spinner />;
    }

    // If authenticated, we already navigated; render nothing to avoid flicker
    if (user) {
        return null;
    }

    // Not authenticated but has token: show the login page
    return <>{children}</>;
};

export default AuthRoute;